import { populateForm, formToObject, objectToQueryString, queryStringToObject } from "../formUtils.js"

class BlogFilters {
  constructor() {
    this.baseUrl = window.location.pathname

    this.state = {
      formData: this.queryParamsAsObject(),
    }

    this.initComponents()
    this.bindEvents()

    // Skip populating the form on first load to preserve server-rendered checkbox state
    this.isUsingServerData = true
    this.render(this.state)
    this.isUsingServerData = false
  }

  initComponents() {
    this.form = document.querySelector("form[name=blog-filters]")
    this.filterInputs = document.querySelectorAll("[data-filters-checkbox]")
    this.clearButton = document.querySelector("[data-filters-clear]")
    this.sortSelect = document.querySelector("[data-filters-sort]")

    this.articlesContainer = document.querySelector("[data-articles-container]")
    this.summaries = document.querySelectorAll("[data-article-summary]")
    this.spinner = document.querySelector("[data-spinner]")

    document.querySelectorAll(".filter-accordion__item").forEach(el => this.filterAccordionToggle(el))
  }

  // Using custom accordion toggle for filter items as the existing one relies heavily on checkbox states
  // and we need to use checkboxes for the filtering
  filterAccordionToggle(el) {
    const trigger = el.querySelector(".filter-accordion__header")
    const content = el.querySelector(".filter-accordion__selection-wrapper")
    if (!trigger || !content) return

    const isActive = el.classList.contains("filter-accordion__item--active")
    content.setAttribute("aria-hidden", !isActive)
    trigger.setAttribute("aria-expanded", isActive)
    this.updateTabIndex(content, isActive)

    trigger.addEventListener("click", e => {
      e.preventDefault()
      const isNowActive = el.classList.toggle("filter-accordion__item--active")
      content.setAttribute("aria-hidden", !isNowActive)
      trigger.setAttribute("aria-expanded", isNowActive)
      this.updateTabIndex(content, isNowActive)
    })

    trigger.addEventListener("keydown", e => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault()
        const isNowActive = el.classList.toggle("filter-accordion__item--active")
        content.setAttribute("aria-hidden", !isNowActive)
        trigger.setAttribute("aria-expanded", isNowActive)
        this.updateTabIndex(content, isNowActive)
      }
    })
  }

  updateTabIndex(content, isActive) {
    const focusableElements = content.querySelectorAll("input, button, select, textarea, a")
    focusableElements.forEach(el => {
      el.setAttribute("tabindex", isActive ? "0" : "-1")
    })
  }

  bindEvents() {
    // Trigger an update on the form data when the user interacts with the checkbox filters
    this.filterInputs.forEach(input => {
      input.addEventListener("change", () => {
        // Update form data and trigger update
        this.state.formData = formToObject(this.form)
        this.pushStateToHistory()
        this.getArticles()
      })
    })

    // Trigger an update on the form data when the user interacts with the clear button
    if (this.clearButton) {
      this.clearButton.addEventListener("click", () => {
        event.preventDefault()

        // clear filters
        this.state.formData = {}

        this.pushStateToHistory()
        this.getArticles()
      })
    }

    // Trigger an update on the form data when the user interacts with the sort select
    if (this.sortSelect) {
      this.sortSelect.addEventListener("change", () => {
        this.state.formData = formToObject(this.form)
        this.pushStateToHistory()
        this.getArticles()
      })
    }

    // Browser back/forward events will re-populate form values
    window.onpopstate = () => {
      this.state.formData = this.queryParamsAsObject()
      this.getArticles()
    }

    // Clear all filters button
    this.clearAllButton = document.querySelector("[data-clear-all-filters]")
    if (this.clearAllButton) {
      this.clearAllButton.addEventListener("click", () => {
        const preserved = {}
        if (this.state.formData.sort) {
          preserved.sort = this.state.formData.sort
        }

        this.state.formData = preserved
        this.pushStateToHistory()
        this.getArticles()
      })
    }
  }

  // Build out the active filters and clear filters buttons
  renderActiveFilters() {
    const container = document.querySelector("[data-active-filters]")
    if (!container) return

    const activeFiltersContainer = container.querySelector(".blog-summaries__active-filters")
    const clearAllButton = container.querySelector("[data-clear-all-filters]")

    if (!activeFiltersContainer || !clearAllButton) return

    activeFiltersContainer.innerHTML = ""

    const formData = this.state.formData
    let hasFilters = false

    // Exclude the sort key from the active filters
    const excludedKeys = ["sort"]

    for (const key in formData) {
      if (!formData.hasOwnProperty(key)) continue
      if (excludedKeys.includes(key.replace(/\[\]$/, ""))) continue

      const values = Array.isArray(formData[key]) ? formData[key] : [formData[key]]
      if (values.length === 0) continue

      hasFilters = true

      values.forEach(item => {
        // Check against the input values to ensure it is valid
        const input = [...this.filterInputs].find(input => input.value === item)
        if (!input) {
          return
        }

        // Pull in the label text for the filter item
        let labelText = item
        const label = this.form.querySelector(`label[for="${input.id}"]`)
        if (label) {
          labelText = label.textContent.trim()
        }

        const button = document.createElement("button")
        button.type = "button"
        button.className = "blog-summaries__active-filter"
        button.innerHTML = `<span>${labelText}</span><span class="blog-summaries__remove material-icons" aria-hidden="true">close</span>`
        button.setAttribute("aria-label", `Remove ${labelText} filter`)
        button.setAttribute("data-remove-key", key)
        button.setAttribute("data-remove-value", item)

        button.addEventListener("click", () => {
          this.removeFilter(key, item)
        })

        activeFiltersContainer.appendChild(button)
      })
    }

    // Show/hide clear all button
    clearAllButton.classList.toggle("d-none", !hasFilters)
  }

  // Remove a filter from the form data and update
  removeFilter(key, value) {
    const current = this.state.formData[key]

    if (Array.isArray(current)) {
      this.state.formData[key] = current.filter(v => v !== value)
      if (this.state.formData[key].length === 0) {
        delete this.state.formData[key]
      }
    } else if (current === value) {
      delete this.state.formData[key]
    }

    this.pushStateToHistory()
    this.getArticles()
  }

  // eslint-disable-next-line no-unused-vars
  render(props = {}) {
    // Only populate after initial render to sync checkbox state
    if (!this.isUsingServerData) {
      populateForm(this.form, this.state.formData)
    }

    if (!this.clearButton) {
      return
    }
    const isEmpty = Object.keys(this.state.formData).length === 0
    this.clearButton.classList.toggle("active", isEmpty)

    this.renderActiveFilters()

    // show news summaries
    this.summaries.forEach((summary, index) => {
      let delay = index * 150
      setTimeout(this.fadeIn(summary), delay)
    })
  }

  fadeIn(summary) {
    return function() {
      summary.classList.remove("article-summary-hidden")
    }
  }

  actionClearFilters() {
    this.render(this.state)

    this.getArticles()
  }

  queryParamsAsObject() {
    const queryParams = window.location.search.replace(/^\?*/, "")
    return queryStringToObject(queryParams)
  }

  pushStateToHistory() {
    const isEmpty = Object.keys(this.state.formData).length === 0
    if (!isEmpty) {
      const queryParams = objectToQueryString(this.state.formData)
      window.history.pushState({}, "", this.baseUrl + "?" + queryParams)
    } else {
      window.history.pushState({}, "", this.baseUrl)
    }
  }

  getArticles() {
    // update view
    this.spinner.classList.remove("d-none")
    this.articlesContainer.innerHTML = null

    // send request
    const queryParams = objectToQueryString(this.state.formData)
    var xhr = new XMLHttpRequest()
    xhr.open("GET", this.baseUrl + "?" + queryParams)
    xhr.setRequestHeader("X-Requested-With", "XMLHttpRequest")
    xhr.onload = () => this.replaceArticles(xhr.status, xhr.response)
    xhr.send()
  }

  replaceArticles(status, articlesHtml) {
    this.spinner.classList.add("d-none")

    this.articlesContainer.innerHTML = articlesHtml
    this.summaries = document.querySelectorAll("[data-article-summary]")
    this.render(this.state)

    // Replace the static result count with the count from the rendered include
    const newCount = this.articlesContainer.querySelector("[data-results-count]")
    const visibleCount = document.querySelector("p[data-results-count]:not(.d-none)")

    if (newCount && visibleCount) {
      visibleCount.innerHTML = newCount.innerHTML
    }
  }
}

export default function init() {
  if (document.querySelector(".NewsHub")) {
    new BlogFilters()
  }
}
